import { Tile } from '@carbon/react';
import type { FC } from 'react';
import { formatNumber } from 'utils/number';
import type { WellEurMetrics } from 'types/api-responses';
import styles from './well-eur-strip.module.scss';

/** What a tile shows when the source reports no figure. */
const EMPTY = '—';

interface Props {
  /**
   * 30-year EUR per stream as the backend reports it: totals in bbl / Mcf plus the
   * lateral-normalized values, from the well's promoted pdp_default DCA forecast. The
   * per-foot values are served, not derived, so they match that forecast's lateral.
   */
  eur?: WellEurMetrics | null;
}

// Totals arrive in bbl / Mcf and are shown in Mbbl / MMcf so the numbers read at a
// glance; the per-foot values are already normalized and pass through untouched.
const TILES: Array<{
  id: string;
  label: string;
  value: (eur: WellEurMetrics) => number | null;
}> = [
  { id: 'oil-eur', label: 'Oil EUR (Mbbl)', value: (e) => scale(e.oilBbl) },
  { id: 'oil-eur-ft', label: 'Oil EUR (bbl/ft)', value: (e) => e.oilBblPerFt },
  { id: 'gas-eur', label: 'Gas EUR (MMcf)', value: (e) => scale(e.gasMcf) },
  { id: 'gas-eur-ft', label: 'Gas EUR (Mcf/ft)', value: (e) => e.gasMcfPerFt },
  {
    id: 'water-eur',
    label: 'Water EUR (Mbbl)',
    value: (e) => scale(e.waterBbl),
  },
  {
    id: 'water-eur-ft',
    label: 'Water EUR (bbl/ft)',
    value: (e) => e.waterBblPerFt,
  },
];

function scale(value: number | null): number | null {
  return value === null ? null : value / 1000;
}

/**
 * An em-dash where the source reports nothing, rather than a "0.0" that would read as
 * a measured value. Zero counts as nothing here: the backend stores 0 for a stream it
 * has no figure for, so a real zero EUR and a missing one are indistinguishable.
 * @param {number | null} value The scaled or per-foot figure.
 * @return {string} One decimal place with separators, or an em-dash.
 */
function format(value: number | null): string {
  if (value === null || value <= 0) return EMPTY;

  return formatNumber({
    value,
    minPrecision: 1,
    maxPrecision: 1,
    includeCommas: true,
    fallback: EMPTY,
  });
}

/**
 * The EUR strip: six tiles, one per stream per basis.
 *
 * A KPI strip for inside a white card, so the tiles take the light grey layer to read
 * against the card surface, the inverse of the wells listing. Shared by the Well
 * Workspace's EUR card and the Well page's EUR section.
 * @param {Props} props The well's EUR figures.
 * @return {JSX.Element} The strip.
 */
export const WellEurStrip: FC<Props> = ({ eur }) => (
  <div className={styles.strip}>
    {TILES.map((tile) => (
      <Tile key={tile.id} className={styles.tile}>
        <div className={styles.header}>
          <span className={styles.label}>{tile.label}</span>
        </div>
        <span className={styles.count}>
          {format(eur ? tile.value(eur) : null)}
        </span>
      </Tile>
    ))}
  </div>
);
